import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  ArrowLeft,
  Loader2,
  RefreshCw,
  FileText,
  Eye,
  Edit,
  Trash2,
  Plus,
  Upload,
  Target,
  ExternalLink
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const UseCaseManagerPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  const [useCases, setUseCases] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUseCase, setEditingUseCase] = useState(null);
  const [form, setForm] = useState({
    title: '',
    slug: '',
    subtitle: '',
    hero_description: '',
    pain_points: '',
    benefits: '',
    cta_title: '',
    cta_description: '',
    meta_title: '',
    meta_description: '',
    featured_image: '',
    status: 'draft',
    featured_tools: [],
    featured_articles: []
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  
  // Available free tools
  const FREE_TOOLS = [
    { id: 'name-splitter', name: 'Name Splitter', path: '/tools/name-splitter' },
    { id: 'csv-doctor', name: 'CSV Doctor', path: '/tools/csv-doctor' },
    { id: 'phone-formatter', name: 'Phone Formatter', path: '/tools/phone-formatter' },
    { id: 'gender-detector', name: 'Gender Detector', path: '/tools/gender-detector' },
    { id: 'email-validator', name: 'Email Validator', path: '/tools/email-validator' },
    { id: 'email-formatter', name: 'Email Formatter', path: '/tools/email-formatter' },
    { id: 'email-to-name', name: 'Email to Name', path: '/tools/email-to-name' },
  ];

  useEffect(() => {
    if (!authLoading && user) {
      fetchUseCases();
      fetchBlogPosts();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user]);
  
  const fetchBlogPosts = async () => {
    try {
      const response = await api.get('/api/admin/blog/posts');
      setBlogPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    }
  };

  const fetchUseCases = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const response = await api.get('/api/admin/use-cases');
      setUseCases(response.data.use_cases || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load use cases');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUseCase(null);
    setForm({
      title: '',
      slug: '',
      subtitle: '',
      hero_description: '',
      pain_points: '',
      benefits: '',
      cta_title: 'Start Cleaning Your Data',
      cta_description: 'Upload your file and clean it in minutes. 100 free credits on signup.',
      meta_title: '',
      meta_description: '',
      featured_image: '',
      status: 'draft',
      featured_tools: [],
      featured_articles: []
    });
    setDialogOpen(true);
  };

  const handleEdit = async (useCase) => {
    try {
      const response = await api.get(`/api/admin/use-cases/${useCase.use_case_id}`);
      const fullUseCase = response.data.use_case;
      setEditingUseCase(fullUseCase);
      setForm({
        title: fullUseCase.title || '',
        slug: fullUseCase.slug || '',
        subtitle: fullUseCase.subtitle || '',
        hero_description: fullUseCase.hero_description || '',
        pain_points: (fullUseCase.pain_points || []).join('\n'),
        benefits: (fullUseCase.benefits || []).join('\n'),
        cta_title: fullUseCase.cta_title || 'Start Cleaning Your Data',
        cta_description: fullUseCase.cta_description || '',
        meta_title: fullUseCase.meta_title || '',
        meta_description: fullUseCase.meta_description || '',
        featured_image: fullUseCase.featured_image || '',
        status: fullUseCase.status || 'draft',
        featured_tools: fullUseCase.featured_tools || [],
        featured_articles: fullUseCase.featured_articles || []
      });
      setDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load use case');
    }
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) {
      toast.error('Title and slug are required');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        pain_points: form.pain_points.split('\n').map(p => p.trim()).filter(p => p),
        benefits: form.benefits.split('\n').map(b => b.trim()).filter(b => b)
      };
      
      if (editingUseCase) {
        await api.put(`/api/admin/use-cases/${editingUseCase.use_case_id}`, data);
        toast.success('Use case updated');
      } else {
        await api.post('/api/admin/use-cases', data);
        toast.success('Use case created');
      }
      
      setDialogOpen(false);
      await fetchUseCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save use case');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (useCase) => {
    if (!confirm(`Delete "${useCase.title}"?`)) return;
    
    try {
      await api.delete(`/api/admin/use-cases/${useCase.use_case_id}`);
      toast.success('Use case deleted');
      setUseCases(useCases.filter(u => u.use_case_id !== useCase.use_case_id));
    } catch (error) {
      toast.error('Failed to delete use case');
    }
  };

  const toggleTool = (toolId) => {
    setForm(prev => {
      const currentTools = prev.featured_tools || [];
      if (currentTools.includes(toolId)) {
        return { ...prev, featured_tools: currentTools.filter(t => t !== toolId) };
      } else if (currentTools.length < 3) {
        return { ...prev, featured_tools: [...currentTools, toolId] };
      } else {
        toast.error('Maximum 3 tools allowed');
        return prev;
      }
    });
  };

  const toggleArticle = (postId) => {
    setForm(prev => {
      const currentArticles = prev.featured_articles || [];
      if (currentArticles.includes(postId)) {
        return { ...prev, featured_articles: currentArticles.filter(a => a !== postId) };
      } else if (currentArticles.length < 3) {
        return { ...prev, featured_articles: [...currentArticles, postId] };
      } else {
        toast.error('Maximum 3 articles allowed');
        return prev;
      }
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit.');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/api/admin/blog/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const imageUrl = baseUrl + response.data.url;
      
      setForm(f => ({ ...f, featured_image: imageUrl }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-4">You don&apos;t have permission to view this page.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900">CRM Data Cleaner</span>
              </Link>
              <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded">ADMIN</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={fetchUseCases}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Use Case Manager</h1>
            <p className="text-slate-600 mt-1">Create SEO landing pages for different use cases</p>
          </div>
          <Button onClick={handleCreate} data-testid="create-use-case-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Use Case
          </Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200">
          {useCases.length === 0 ? (
            <div className="p-12 text-center">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No use cases yet</h3>
              <p className="text-slate-500 mb-4">Create targeted landing pages for different CRM use cases</p>
              <Button onClick={handleCreate}>Create Use Case</Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {useCases.map((useCase) => (
                  <TableRow key={useCase.use_case_id} data-testid={`use-case-row-${useCase.use_case_id}`}>
                    <TableCell>
                      <p className="font-medium text-slate-900">{useCase.title}</p>
                      <p className="text-sm text-slate-500">{useCase.subtitle}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-600">/use-case/{useCase.slug}</p>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        useCase.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {useCase.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {useCase.status === 'published' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => window.open(`/use-case/${useCase.slug}`, '_blank')}
                            data-testid={`view-use-case-${useCase.use_case_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleEdit(useCase)}
                          data-testid={`edit-use-case-${useCase.use_case_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-600" 
                          onClick={() => handleDelete(useCase)}
                          data-testid={`delete-use-case-${useCase.use_case_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Quick Create Suggestions - Dynamic based on existing use cases */}
        <div className="mt-8 bg-indigo-50 border border-indigo-100 rounded-xl p-6">
          <h3 className="font-semibold text-indigo-900 mb-3">Suggested Use Cases to Create</h3>
          <div className="flex flex-wrap gap-2">
            {(() => {
              // All possible suggestions
              const allSuggestions = [
                'clean-hubspot-contacts', 'clean-salesforce-import', 'clean-webinar-attendees', 
                'clean-linkedin-exports', 'clean-mailchimp-lists', 'clean-event-registrations',
                'clean-zoho-crm-data', 'clean-pipedrive-contacts', 'clean-airtable-exports',
                'clean-google-sheets-data', 'clean-excel-contacts', 'clean-survey-responses',
                'clean-newsletter-subscribers', 'clean-lead-lists', 'clean-customer-database',
                'clean-vendor-contacts', 'clean-conference-attendees', 'clean-membership-data'
              ];
              
              // Filter out existing slugs
              const existingSlugs = useCases.map(uc => uc.slug);
              const availableSuggestions = allSuggestions.filter(s => !existingSlugs.includes(s));
              
              // Show first 8 available suggestions
              return availableSuggestions.slice(0, 8).map(slug => (
                <button
                  key={slug}
                  onClick={() => {
                    const title = slug.split('-').slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                    setEditingUseCase(null);
                    setForm({
                      title: `Clean ${title}`,
                      slug: slug,
                      subtitle: `Import clean ${title.toLowerCase()} to your CRM`,
                      hero_description: '',
                      pain_points: '',
                      benefits: '',
                      cta_title: 'Start Cleaning Your Data',
                      cta_description: 'Upload your file and clean it in minutes. 100 free credits on signup.',
                      meta_title: '',
                      meta_description: '',
                      featured_image: '',
                      status: 'draft'
                    });
                    setDialogOpen(true);
                  }}
                  className="px-3 py-1.5 bg-white border border-indigo-200 rounded-full text-sm text-indigo-700 hover:bg-indigo-100 transition-colors"
                >
                  {slug}
                </button>
              ));
            })()}
          </div>
          {useCases.length > 0 && (
            <p className="text-xs text-indigo-600 mt-3">
              {useCases.filter(uc => uc.status === 'published').length} published, {useCases.filter(uc => uc.status === 'draft').length} drafts
            </p>
          )}
        </div>
      </main>

      {/* Use Case Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUseCase ? 'Edit Use Case' : 'Create New Use Case'}</DialogTitle>
            <DialogDescription>
              Create a targeted landing page for a specific CRM use case.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Title *</label>
                <Input 
                  value={form.title}
                  onChange={(e) => {
                    setForm({ ...form, title: e.target.value });
                    if (!editingUseCase) {
                      setForm(f => ({ ...f, slug: generateSlug(e.target.value) }));
                    }
                  }}
                  placeholder="Clean HubSpot Contacts"
                  data-testid="use-case-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Slug *</label>
                <Input 
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="clean-hubspot-contacts"
                  data-testid="use-case-slug-input"
                />
                <p className="text-xs text-slate-500 mt-1">/use-case/{form.slug || 'your-slug'}</p>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Subtitle</label>
              <Input 
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Import clean contacts to HubSpot in minutes"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Hero Description</label>
              <Textarea 
                value={form.hero_description}
                onChange={(e) => setForm({ ...form, hero_description: e.target.value })}
                placeholder="Describe the use case and the problem it solves..."
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Pain Points (one per line)</label>
                <Textarea 
                  value={form.pain_points}
                  onChange={(e) => setForm({ ...form, pain_points: e.target.value })}
                  placeholder="Duplicate contacts in your CRM
Invalid email formats
Missing phone country codes"
                  rows={4}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Benefits (one per line)</label>
                <Textarea 
                  value={form.benefits}
                  onChange={(e) => setForm({ ...form, benefits: e.target.value })}
                  placeholder="Clean data in seconds
Standardize formats automatically
Detect and fix typos"
                  rows={4}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">CTA Title</label>
                <Input 
                  value={form.cta_title}
                  onChange={(e) => setForm({ ...form, cta_title: e.target.value })}
                  placeholder="Start Cleaning Your Data"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">CTA Description</label>
                <Input 
                  value={form.cta_description}
                  onChange={(e) => setForm({ ...form, cta_description: e.target.value })}
                  placeholder="Upload your file and clean it in minutes."
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Meta Title (SEO)</label>
                <Input 
                  value={form.meta_title}
                  onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                  placeholder="Clean HubSpot Contacts - Free CRM Data Cleaner"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Featured Image</label>
                <div className="flex gap-2">
                  <Input 
                    value={form.featured_image}
                    onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                    placeholder="https://..."
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                    <Button type="button" variant="outline" disabled={uploadingImage} asChild>
                      <span>
                        {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Meta Description (SEO)</label>
              <Textarea 
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                placeholder="Clean and standardize your HubSpot contacts before import. Fix emails, phone numbers, and more."
                rows={2}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Status</label>
              <select 
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-use-case-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingUseCase ? 'Update' : 'Create'} Use Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UseCaseManagerPage;
